import React, { useEffect, useState } from 'react';
import '../ForBrands/brandMode.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllUserPaymentHistory } from '../../store/actions/userActions';
import { Card, EmptyState, Field, Loading, MetricCard, SearchBox, Select } from '../ForBrands/ui/BrandUI';
import { PAYMENT_STATUS, PaymentStatusBadge, PaymentsPager, TD_CLASS, TD_MUTED_CLASS, TH_CLASS, TR_CLASS } from './paymentStatus';

// The customer's plan: a known subscription status by name (trialing reads
// as Trial), no status or an unknown one as Free.
const subscriptionKey = (status) => (status && PAYMENT_STATUS[status] ? status : 'free');

const PaymentHistoryTab = () => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state?.user?.allUserPaymentHistory || []);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchAllUserPaymentHistory()).finally(() => {
      setLoading(false);
    });
  }, [dispatch]);

  useEffect(() => {
    // Apply filters
    let filtered = [...users];

    // Search by name or email
    if (searchQuery.trim()) {
      filtered = filtered.filter(user =>
        user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by subscription status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => {
        const subStatus = user.subscription?.status;
        // Handle 'free' filter - users with no subscription or free tier
        if (statusFilter === 'free') {
          return !subStatus || subStatus === 'free';
        }
        return subStatus === statusFilter;
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  }, [users, searchQuery, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Calculate totals
  const totalRevenue = users.reduce((sum, user) => {
    const userTotal = user.subscription?.paymentHistory?.reduce((pSum, p) =>
      p.status === 'success' ? pSum + (p.amount || 0) : pSum, 0) || 0;
    return sum + userTotal;
  }, 0);

  const totalPayments = users.reduce((sum, user) =>
    sum + (user.subscription?.paymentHistory?.filter(p => p.status === 'success').length || 0), 0);

  if (loading) {
    return <Loading label="Loading payment history..." />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <MetricCard label="Total revenue" value={`$${totalRevenue.toFixed(2)}`} />
        <MetricCard label="Total payments" value={totalPayments} />
        <MetricCard label="Active customers" value={filteredUsers.length} />
      </div>

      <Card padding="p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Search">
            <SearchBox value={searchQuery} onChange={setSearchQuery} placeholder="Name or email..." />
          </Field>
          <Field label="Subscription status">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="trialing">Trialing</option>
              <option value="canceling">Canceling</option>
              <option value="canceled">Canceled</option>
              <option value="free">Free</option>
              <option value="past_due">Past due</option>
              <option value="incomplete">Incomplete</option>
              <option value="unpaid">Unpaid</option>
            </Select>
          </Field>
          <Field label="Items per page">
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={30}>30 per page</option>
              <option value={100}>100 per page</option>
            </Select>
          </Field>
        </div>
      </Card>

      {currentUsers.length === 0 ? (
        <EmptyState>No payment history found</EmptyState>
      ) : (
        <div className="flex flex-col gap-4">
          {currentUsers.map((user) => {
            const payments = user.subscription?.paymentHistory || [];
            const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
            return (
              <Card key={user.email} padding="p-0" className="overflow-hidden" as="section" aria-label={name || user.email}>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-bm-border px-6 py-4">
                  <div className="min-w-0">
                    <h3 className="bm-h-s text-bm-ink">{name || user.email}</h3>
                    <p className="bm-body-s text-bm-muted">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bm-body-s text-bm-muted">Subscription</span>
                    <PaymentStatusBadge status={subscriptionKey(user.subscription?.status)} />
                  </div>
                </div>

                {payments.length > 0 ? (
                  <div className="w-full overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-bm-subtle">
                        <tr>
                          <th className={TH_CLASS}>Date</th>
                          <th className={TH_CLASS}>Amount</th>
                          <th className={TH_CLASS}>Billing cycle</th>
                          <th className={TH_CLASS}>Status</th>
                          <th className={TH_CLASS}>Billing address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment, idx) => (
                          <tr key={idx} className={TR_CLASS}>
                            <td className={`${TD_MUTED_CLASS} whitespace-nowrap`}>
                              {payment.date ? new Date(payment.date).toLocaleDateString() : '-'}
                            </td>
                            <td className={`${TD_CLASS} whitespace-nowrap`}>
                              <span className="bm-label-l">${payment.amount?.toFixed(2) ?? '0.00'}</span>
                            </td>
                            <td className={TD_MUTED_CLASS}>{payment.billingCycle || '-'}</td>
                            <td className={TD_CLASS}>
                              <PaymentStatusBadge status={payment.status} />
                            </td>
                            <td className={TD_MUTED_CLASS}>{payment.billingAddress || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="px-6 py-5 text-center bm-body-m text-bm-muted">No payments recorded</p>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="bm-body-s text-bm-muted">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} results
          </div>
          <PaymentsPager page={currentPage} pageCount={totalPages} onChange={setCurrentPage} />
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryTab;
